import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../utils/analytics';
import { useUser } from '../hooks/useUser';
import { getAiTrainingQuestion, getAiTrainingSession, isApiError, saveAiTrainingAnswer, startAiTraining, submitAiTraining } from '../services/api';
import { deadlineAfterHold, secondsUntil } from '../utils/examTimer';
import { GenerationFailedNotice } from '../components/GenerationFailedNotice';
import { clampIndex, clearAiTrainingInProgress, readAiTrainingInProgress, writeAiTrainingInProgress } from '../utils/examProgress';
import { CertQuestion, CertQuestionType, CertAnswer } from '@unity-learning/shared';
import {
    Clock,
    ChevronLeft,
    ChevronRight,
    Flag,
    RefreshCw,
    AlertTriangle,
    List,
    Sparkles,
    Loader2
} from 'lucide-react';

// Import question components
import { QuestionMatrix } from '../components/questions/QuestionMatrix';
import { QuestionMCQSingle } from '../components/questions/QuestionMCQSingle';
import { QuestionMatchingDrag } from '../components/questions/QuestionMatchingDrag';
import { QuestionOrdering } from '../components/questions/QuestionOrdering';
import { QuestionCodeDropdown } from '../components/questions/QuestionCodeDropdown';
import { QuestionCodeConstruction } from '../components/questions/QuestionCodeConstruction';
import { QuestionCodeInput } from '../components/questions/QuestionCodeInput';
import { QuestionBoolean } from '../components/questions/QuestionBoolean';
import { QuestionMCQMulti } from '../components/questions/QuestionMCQMulti';
import { QuestionPrompt } from '../components/questions/QuestionPrompt';

interface CertQuestionAnswer {
    questionId: string;
    response: CertAnswer | null;
    isFlagged: boolean;
    isAnswered: boolean;
}

interface BlueprintItem {
    questionNumber: number;
    type: string;
    topic: string;
}

// Chrono : le temps restant vient d'une échéance fixée au démarrage (voir utils/examTimer), pas d'un
// décompte de ticks ; une échéance explicite reprend une session interrompue sans rendre de temps.
function useCountdown(initialMinutes: number, onExpire: () => void) {
    const totalSeconds = initialMinutes * 60;
    const [deadline, setDeadline] = useState<number | null>(null);
    const [isRunning, setIsRunning] = useState(false);
    // En pause pendant qu'une question se génère : l'échéance est repoussée de l'attente à la reprise.
    const heldSinceRef = useRef<number | null>(null);
    const [isHeld, setIsHeld] = useState(false);
    const [now, setNow] = useState(() => Date.now());
    const expiredRef = useRef(false);

    // Toujours la dernière version du rappel : l'envoi automatique doit voir la session actuelle.
    const onExpireRef = useRef(onExpire);
    onExpireRef.current = onExpire;

    useEffect(() => {
        if (!isRunning || isHeld || deadline === null) return;

        const tick = () => {
            const t = Date.now();
            setNow(t);
            if (!expiredRef.current && secondsUntil(deadline, t) === 0) {
                expiredRef.current = true;
                onExpireRef.current();
            }
        };

        tick();
        const interval = setInterval(tick, 500);
        document.addEventListener('visibilitychange', tick);
        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', tick);
        };
    }, [isRunning, isHeld, deadline]);

    const start = (at?: number) => {
        // Une seule lecture de l'horloge : sinon l'affichage figé d'une pause immédiate montre une seconde de trop.
        const startedAt = Date.now();
        setNow(startedAt);
        setDeadline(d => d ?? at ?? startedAt + totalSeconds * 1000);
        setIsRunning(true);
    };
    const pause = () => setIsRunning(false);
    // Une attente qui ne dépend pas de l'élève (génération d'une question) ne lui coûte pas de temps.
    // Limite : un F5 pendant la pause reprend l'échéance enregistrée avant elle.
    const hold = () => {
        if (heldSinceRef.current !== null) return;
        heldSinceRef.current = Date.now();
        setNow(heldSinceRef.current);
        setIsHeld(true);
    };
    const release = () => {
        const heldSince = heldSinceRef.current;
        if (heldSince === null) return;
        heldSinceRef.current = null;
        const releasedAt = Date.now();
        setNow(releasedAt); // sinon le chrono montre une image avec le temps de la pause en trop
        setDeadline(d => (d === null ? d : deadlineAfterHold(d, heldSince, releasedAt)));
        setIsHeld(false);
    };

    const secondsRemaining = deadline === null ? totalSeconds : secondsUntil(deadline, now);
    const minutes = Math.floor(secondsRemaining / 60);
    const seconds = secondsRemaining % 60;
    const formatted = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

    return { secondsRemaining, minutes, seconds, formatted, start, pause, hold, release, isRunning, isHeld, deadline };
}


// Loading Component for Blueprint Generation
const BlueprintGenerator = () => {
    const [step, setStep] = useState(0);
    const steps = [
        "Analyse du programme Unity…",
        "Sélection des sujets à évaluer…",
        "Calibrage de la difficulté…",
        "Génération du plan de l'examen…",
        "Finalisation de la session…"
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            setStep(s => (s < steps.length - 1 ? s + 1 : s));
        }, 1500);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="w-full flex flex-col items-center justify-center py-6">
            <div className="relative w-20 h-20 mb-6">
                <div className="absolute inset-0 border-4 border-purple-100 dark:border-purple-900/30 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-purple-600 rounded-full border-t-transparent animate-spin"></div>
                <Sparkles className="absolute inset-0 m-auto h-8 w-8 text-purple-600 animate-pulse" />
            </div>
            <div className="text-xl font-medium_ text-gray-800 dark:text-gray-100 mb-2">
                Orchestrateur IA
            </div>
            <div className="text-sm text-purple-600 dark:text-purple-400 font-medium animate-pulse">
                {steps[step]}
            </div>
        </div>
    );
};

// Loading Component for Question Generation
const QuestionGenerator = ({ topic }: { topic?: string }) => {
    const [step, setStep] = useState(0);
    const steps = [
        `Consultation de la base de connaissances${topic ? ' : ' + topic : ''}…`,
        "Construction du scénario…",
        "Génération des distracteurs…",
        "Validation de la logique…",
        "Affichage de la question…"
    ];

    useEffect(() => {
        setStep(0);
        const interval = setInterval(() => {
            setStep(s => (s < steps.length - 1 ? s + 1 : s));
        }, 1200); // Slightly faster for questions
        return () => clearInterval(interval);
    }, [topic]);

    return (
        <div className="flex flex-col items-center justify-center py-32">
            <div className="relative w-24 h-24 mb-8">
                {/* Outer ring */}
                <div className="absolute inset-0 border-4 border-blue-100 dark:border-blue-900/30 rounded-full animate-ping opacity-20"></div>
                {/* Spinning ring */}
                <div className="absolute inset-0 border-4 border-blue-500 rounded-full border-r-transparent border-b-transparent animate-spin"></div>
                {/* Inner Icon */}
                <div className="absolute inset-0 flex items-center justify-center">
                    <RefreshCw className="w-8 h-8 text-blue-500 animate-spin-reverse" />
                </div>
            </div>

            <h3 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-purple-600 mb-4">
                Génération de la question
            </h3>

            <div className="flex flex-col items-center gap-2">
                <div className="text-gray-500 dark:text-gray-400 text-sm uppercase tracking-wider font-semibold">
                    Étape en cours
                </div>
                <div className="text-lg text-gray-800 dark:text-gray-200 font-medium min-h-[1.75rem] transition-all duration-300">
                    {steps[step]}
                </div>
            </div>
        </div>
    );
};

export const AITrainingPage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useUser();

    // Session state
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [blueprint, setBlueprint] = useState<BlueprintItem[]>([]);
    const [durationMinutes, setDurationMinutes] = useState(50);
    const [questionCount, setQuestionCount] = useState(40);

    // Training state
    const [currentIndex, setCurrentIndex] = useState(0);
    const [currentQuestion, setCurrentQuestion] = useState<CertQuestion | null>(null);
    const [answers, setAnswers] = useState<Record<string, CertQuestionAnswer>>({});
    const [isLoading, setIsLoading] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    // Une question que le modèle n'a pas pu générer : message et « Réessayer » à sa place, chrono en pause.
    const [questionFailure, setQuestionFailure] = useState<{ index: number; message: string } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [trainingStarted, setTrainingStarted] = useState(false);
    const [showSummary, setShowSummary] = useState(false);
    const [showConfirmFinish, setShowConfirmFinish] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Cache for loaded questions to allow prefetching
    const [questionCache, setQuestionCache] = useState<Record<number, CertQuestion>>({});

    // Timer (le hook garde toujours la dernière version du rappel : pas de fermeture périmée)
    const timer = useCountdown(durationMinutes, () => handleSubmit('timeout'));

    // Reprise d'une session interrompue (F5, onglet fermé) : une seule tentative par chargement.
    const resumeAttemptedRef = useRef(false);
    const [resumeNotice, setResumeNotice] = useState<string | null>(null);

    useEffect(() => {
        if (!user || trainingStarted || resumeAttemptedRef.current) return;
        resumeAttemptedRef.current = true;
        const saved = readAiTrainingInProgress(user.id);
        if (!saved) return;

        const resume = async () => {
            setIsLoading(true);
            try {
                const session = await getAiTrainingSession<BlueprintItem>(saved.sessionId);
                const cache: Record<number, CertQuestion> = {};
                for (const { index, question } of session.questions) cache[index] = question;
                const restoredAnswers: Record<string, CertQuestionAnswer> = {};
                for (const [questionId, savedAnswer] of Object.entries(session.answers)) {
                    restoredAnswers[questionId] = { ...savedAnswer, questionId, isFlagged: savedAnswer.isFlagged ?? false, isAnswered: savedAnswer.isAnswered ?? false };
                }

                setQuestionCache(cache);
                setSessionId(session.sessionId);
                setBlueprint(session.blueprint);
                setDurationMinutes(session.durationMinutes);
                setQuestionCount(session.questionCount);
                setAnswers(restoredAnswers);
                setTrainingStarted(true);
                timer.start(saved.deadline);
                trackEvent('ai_training_resumed');

                const index = clampIndex(saved.currentIndex, session.blueprint.length);
                if (cache[index]) {
                    setCurrentQuestion(cache[index]);
                    setCurrentIndex(index);
                } else {
                    await fetchQuestion(session.sessionId, index);
                }
            } catch (err) {
                // Sessions en mémoire côté serveur : après un redémarrage, elle n'existe plus.
                clearAiTrainingInProgress(user.id);
                setResumeNotice(isApiError(err) && !err.isNetworkError
                    ? 'Votre session d\'entraînement précédente n\'est plus disponible (le serveur a redémarré) : vous pouvez en démarrer une nouvelle.'
                    : 'Impossible de reprendre votre session précédente (erreur réseau) : rechargez la page pour réessayer, ou démarrez une nouvelle session.');
            } finally {
                setIsLoading(false);
            }
        };
        resume();
    }, [user, trainingStarted]);

    // La session, son échéance et la question courante sont conservées dans le navigateur.
    useEffect(() => {
        if (!user || !sessionId || !trainingStarted || timer.deadline === null || isSubmitting) return;
        writeAiTrainingInProgress(user.id, { sessionId, deadline: timer.deadline, currentIndex });
    }, [user, sessionId, trainingStarted, timer.deadline, currentIndex, isSubmitting]);

    // Start AI training session
    const handleStartTraining = async () => {
        trackEvent('ai_training_started', { questionCount, durationMinutes });
        setIsLoading(true);
        setError(null);

        try {
            const session = await startAiTraining<BlueprintItem>({ questionCount, durationMinutes });
            setSessionId(session.sessionId);
            setBlueprint(session.blueprint);
            setDurationMinutes(session.durationMinutes);
            setTrainingStarted(true);
            timer.start();

            // Fetch first question
            await fetchQuestion(session.sessionId, 0);
        } catch (err) {
            setError(isApiError(err) && !err.isNetworkError
                ? err.message || 'Impossible de démarrer la session d\'entraînement'
                : 'Erreur réseau au démarrage de la session d\'entraînement');
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch a specific question
    const fetchQuestion = async (sid: string, index: number) => {
        setQuestionFailure(null);
        // Check cache first
        if (questionCache[index]) {
            timer.release();
            setCurrentQuestion(questionCache[index]);
            setCurrentIndex(index);

            // Initialize answer if not exists (sur `prev`, jamais sur une fermeture périmée)
            const cachedQuestion = questionCache[index];
            setAnswers(prev => prev[cachedQuestion.id] ? prev : ({
                ...prev,
                [cachedQuestion.id]: {
                    questionId: cachedQuestion.id,
                    response: getDefaultAnswer(cachedQuestion.type),
                    isFlagged: false,
                    isAnswered: false
                }
            }));
            return;
        }

        setIsGenerating(true);
        timer.hold();

        try {
            const question = await getAiTrainingQuestion(sid, index);

            // Update cache
            setQuestionCache(prev => ({ ...prev, [index]: question }));

            setCurrentQuestion(question);
            setCurrentIndex(index);

            // Initialize answer if not exists (sur `prev`, jamais sur une fermeture périmée)
            setAnswers(prev => prev[question.id] ? prev : ({
                ...prev,
                [question.id]: {
                    questionId: question.id,
                    response: getDefaultAnswer(question.type),
                    isFlagged: false,
                    isAnswered: false
                }
            }));
            timer.release();
        } catch (err) {
            // Le chrono reste en pause tant que la question manque : l'élève n'y est pour rien.
            setCurrentQuestion(null);
            setCurrentIndex(index);
            setQuestionFailure({
                index,
                message: isApiError(err) && !err.isNetworkError
                    ? err.message || 'La question n\'a pas pu être générée. Réessayez dans un instant.'
                    : 'Erreur réseau lors du chargement de la question. Vérifiez votre connexion, puis réessayez.',
            });
        } finally {
            setIsGenerating(false);
        }
    };

    // Prefetch next question
    useEffect(() => {
        // Pendant un échec, on ne précharge pas la suivante : pendant une panne, ce serait un appel perdu de plus.
        if (!sessionId || !trainingStarted || questionFailure) return;

        const nextIndex = currentIndex + 1;

        // Only prefetch if not already in cache and within bounds
        if (nextIndex < blueprint.length && !questionCache[nextIndex]) {
            const prefetch = async () => {
                try {
                    const question = await getAiTrainingQuestion(sessionId, nextIndex);
                    setQuestionCache(prev => ({
                        ...prev,
                        [nextIndex]: question
                    }));
                } catch (e) {
                    console.warn('Prefetch failed', e);
                }
            };
            prefetch();
        }
    }, [currentIndex, sessionId, trainingStarted, blueprint.length, questionCache, questionFailure]);

    // Get default answer
    const getDefaultAnswer = (type: CertQuestionType): CertAnswer | null => {
        switch (type) {
            case CertQuestionType.TYPE_MCQ_SINGLE:
                return '';
            case CertQuestionType.TYPE_MCQ_MULTI:
            case CertQuestionType.TYPE_ORDERING:
                return [];
            case CertQuestionType.TYPE_MATRIX:
            case CertQuestionType.TYPE_MATCHING_DRAG:
            case CertQuestionType.TYPE_CODE_DROPDOWN:
            case CertQuestionType.TYPE_CODE_INPUT:
            case CertQuestionType.TYPE_CODE_CONSTRUCTION:
                return {};
            case CertQuestionType.TYPE_BOOLEAN:
                return null; // vrai ou faux une fois répondu
            default:
                return '';
        }
    };

    // Handle answer change
    const handleAnswerChange = async (questionId: string, response: CertAnswer) => {
        // `questionId` est toujours écrit : si l'état de cette question n'était pas encore initialisé,
        // le serveur rangeait la réponse sous la clé "undefined" et la notait 0.
        const newAnswer = {
            ...answers[questionId],
            questionId,
            isFlagged: answers[questionId]?.isFlagged ?? false,
            response,
            isAnswered: true
        };

        setAnswers(prev => ({
            ...prev,
            [questionId]: newAnswer
        }));

        // Save to server
        if (sessionId) {
            try {
                await saveAiTrainingAnswer(sessionId, newAnswer);
            } catch (err) {
                console.error('Failed to save answer:', err);
            }
        }
    };

    // Toggle flag
    const toggleFlag = async () => {
        if (!currentQuestion) return;

        const newAnswer = {
            ...answers[currentQuestion.id],
            questionId: currentQuestion.id,
            isFlagged: !answers[currentQuestion.id]?.isFlagged
        };

        setAnswers(prev => ({
            ...prev,
            [currentQuestion.id]: newAnswer
        }));

        if (sessionId) {
            try {
                await saveAiTrainingAnswer(sessionId, newAnswer);
            } catch (err) {
                console.error('Failed to save flag:', err);
            }
        }
    };

    // Navigation
    const goToQuestion = async (index: number) => {
        if (index >= 0 && index < blueprint.length && sessionId && !isGenerating) {
            await fetchQuestion(sessionId, index);
            setShowSummary(false);
        }
    };

    // Submit training
    const handleSubmit = async (submissionType: 'manual' | 'timeout' = 'manual') => {
        if (!sessionId || isSubmitting) return;
        setIsSubmitting(true);
        timer.pause();

        try {
            const { attemptId } = await submitAiTraining(sessionId, submissionType);
            if (user) clearAiTrainingInProgress(user.id);
            navigate(`/certification/results/${attemptId}`);
        } catch (err) {
            setError(isApiError(err) && !err.isNetworkError
                ? err.message || 'Impossible d\'envoyer l\'entraînement'
                : 'Erreur réseau lors de l\'envoi de l\'entraînement');
            setIsSubmitting(false);
        }
    };

    // Render question
    const renderQuestion = () => {
        if (!currentQuestion) return null;

        const answer = answers[currentQuestion.id];
        const commonProps = {
            disabled: false,
            onChange: (value: CertAnswer) => handleAnswerChange(currentQuestion.id, value)
        };

        switch (currentQuestion.type) {
            case CertQuestionType.TYPE_MATRIX:
                return <QuestionMatrix question={currentQuestion} value={(answer?.response as Record<string, number>) || {}} {...commonProps} />;
            case CertQuestionType.TYPE_MCQ_SINGLE:
                return <QuestionMCQSingle question={currentQuestion} value={(answer?.response as string) || null} {...commonProps} />;
            case CertQuestionType.TYPE_MATCHING_DRAG:
                return <QuestionMatchingDrag question={currentQuestion} value={(answer?.response as Record<string, string>) || {}} {...commonProps} />;
            case CertQuestionType.TYPE_ORDERING:
                return <QuestionOrdering question={currentQuestion} value={(answer?.response as string[]) || []} {...commonProps} />;
            case CertQuestionType.TYPE_CODE_DROPDOWN:
                return <QuestionCodeDropdown question={currentQuestion} value={(answer?.response as Record<string, string>) || {}} {...commonProps} />;
            case CertQuestionType.TYPE_CODE_CONSTRUCTION:
                return <QuestionCodeConstruction question={currentQuestion} value={(answer?.response as Record<string, string>) || {}} {...commonProps} />;
            case CertQuestionType.TYPE_CODE_INPUT:
                return <QuestionCodeInput question={currentQuestion} value={(answer?.response as Record<string, string>) || {}} {...commonProps} />;
            case CertQuestionType.TYPE_BOOLEAN:
                return <QuestionBoolean question={currentQuestion} value={answer?.response as unknown as boolean | null} {...commonProps} />;
            case CertQuestionType.TYPE_MCQ_MULTI:
                return <QuestionMCQMulti question={currentQuestion} value={(answer?.response as string[]) || []} {...commonProps} />;
            default:
                return <div className="text-red-500">Type de question inconnu : {currentQuestion.type}</div>;
        }
    };

    // Loading state
    if (isLoading && !trainingStarted) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="max-w-lg w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
                    <BlueprintGenerator />
                </div>
            </div>
        );
    }

    // Error state
    if (error && !trainingStarted) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="text-center">
                    <div className="text-red-500 mb-4">{error}</div>
                    <button onClick={() => navigate('/certification')} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">
                        Retour à la certification
                    </button>
                </div>
            </div>
        );
    }

    // Pre-training start screen
    if (!trainingStarted) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="max-w-lg w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
                    <div className="flex items-center gap-3 mb-6">
                        <Sparkles className="w-8 h-8 text-purple-500" />
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                            Entraînement IA
                        </h1>
                    </div>

                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                        Des questions d'entraînement à la certification générées par l'IA : chaque session crée de nouvelles questions, adaptées aux thèmes de la certification Unity.
                    </p>

                    <div className="space-y-4 mb-8">
                        <div className="flex items-center text-gray-600 dark:text-gray-400">
                            <Clock className="w-5 h-5 mr-3" />
                            <span>Durée : {durationMinutes} minutes</span>
                        </div>
                        <div className="flex items-center text-gray-600 dark:text-gray-400">
                            <List className="w-5 h-5 mr-3" />
                            <span>Questions : {questionCount}, générées par l'IA</span>
                        </div>
                    </div>

                    <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-4 mb-8">
                        <div className="flex items-start">
                            <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 mr-2" />
                            <div className="text-sm text-purple-800 dark:text-purple-300">
                                Les questions sont générées en temps réel : un court délai est possible au chargement de chaque question.
                            </div>
                        </div>
                    </div>

                    {resumeNotice && (
                        <div role="status" className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4 mb-6 text-sm text-amber-800 dark:text-amber-300">
                            {resumeNotice}
                        </div>
                    )}

                    <button
                        onClick={handleStartTraining}
                        disabled={isLoading}
                        className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50"
                    >
                        Commencer l'entraînement IA
                    </button>

                    <button
                        onClick={() => navigate('/certification')}
                        className="w-full mt-3 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    >
                        ← Retour à la certification
                    </button>
                </div>
            </div>
        );
    }

    // Answer summary for current question
    const answeredCount = Object.values(answers).filter(a => a.isAnswered).length;
    const progressPercent = (answeredCount / blueprint.length) * 100;

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Header */}
            <div className="sticky top-0 z-40 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="w-full px-6 lg:px-12">
                    <div className="flex items-center justify-between h-16">
                        {/* Navigation */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => goToQuestion(currentIndex - 1)}
                                disabled={currentIndex === 0 || isGenerating}
                                title="Question précédente"
                                aria-label="Question précédente"
                                className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => goToQuestion(currentIndex + 1)}
                                disabled={currentIndex >= blueprint.length - 1 || isGenerating}
                                title="Question suivante"
                                aria-label="Question suivante"
                                className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Timer & Progress */}
                        <div className="flex items-center gap-4">
                            <div className="text-lg font-mono font-bold text-gray-900 dark:text-white">
                                {timer.formatted}
                            </div>
                            {timer.isHeld && (
                                <span
                                    className="text-xs font-medium px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                                    title="Le chrono est arrêté pendant que la question se génère : ce temps ne vous est pas compté."
                                    data-testid="timer-held"
                                >
                                    ⏸ Chrono en pause
                                </span>
                            )}
                            <div className="text-sm text-gray-500">
                                {currentIndex + 1} / {blueprint.length}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                            <button onClick={toggleFlag} title="Marquer la question pour y revenir" aria-label="Marquer la question pour y revenir" className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200">
                                <Flag className={`w-4 h-4 ${currentQuestion && answers[currentQuestion.id]?.isFlagged ? 'text-orange-500' : 'text-gray-500'}`} />
                            </button>
                            <button onClick={() => setShowSummary(true)} className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200">
                                Récapitulatif
                            </button>
                            <button onClick={() => setShowConfirmFinish(true)} className="px-4 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">
                                Terminer
                            </button>
                        </div>
                    </div>

                    {/* Progress bar */}
                    <div className="h-1 bg-gray-200 dark:bg-gray-700">
                        <div className="h-full bg-gradient-to-r from-purple-500 to-blue-500 transition-all" style={{ width: `${progressPercent}%` }} />
                    </div>
                </div>
            </div>

            {/* Question Content */}
            <div className="w-full px-6 lg:px-12 py-8">
                {/* Une erreur pendant l'entraînement (l'envoi surtout) n'était affichée nulle part. */}
                {error && (
                    <div role="alert" className="max-w-3xl mx-auto mb-4 flex items-start gap-3 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-800 dark:text-red-200">
                        <span className="flex-1">{error}</span>
                        <button type="button" onClick={() => setError(null)} className="text-red-500 hover:text-red-700 dark:text-red-300" aria-label="Fermer ce message">✕</button>
                    </div>
                )}
                {isGenerating ? (
                    <QuestionGenerator topic={blueprint[currentIndex]?.topic} />
                ) : questionFailure && sessionId ? (
                    <GenerationFailedNotice
                        className="max-w-3xl mx-auto"
                        title={`La question ${questionFailure.index + 1} n'a pas pu être générée`}
                        message={questionFailure.message}
                        onRetry={() => fetchQuestion(sessionId, questionFailure.index)}
                    />
                ) : currentQuestion && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                        {/* Question header */}
                        <div className="mb-6">
                            <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                                <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded">
                                    Générée par l'IA
                                </span>
                                {currentQuestion.tags?.map(tag => (
                                    <span key={tag} className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                                        {tag}
                                    </span>
                                ))}
                            </div>
                            <QuestionPrompt text={currentQuestion.prompt} className="text-lg max-w-3xl" />
                        </div>

                        {/* Question component */}
                        {renderQuestion()}
                    </div>
                )}
            </div>

            {/* Summary Modal */}
            {showSummary && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-auto m-4 p-6">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Récapitulatif des questions</h3>
                        <div className="grid grid-cols-5 gap-2 mb-6">
                            {blueprint.map((_, index) => {
                                const questionId = `ai_q_${index + 1}`;
                                const answer = answers[questionId];
                                return (
                                    <button
                                        key={index}
                                        onClick={() => goToQuestion(index)}
                                        className={`p-2 rounded-lg text-sm font-medium ${answer?.isAnswered
                                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                            } ${answer?.isFlagged ? 'ring-2 ring-orange-400' : ''}`}
                                    >
                                        {index + 1}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => setShowSummary(false)} className="flex-1 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg">
                                Fermer
                            </button>
                            <button onClick={() => setShowConfirmFinish(true)} className="flex-1 py-2 bg-green-600 text-white rounded-lg">
                                Terminer l'entraînement
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirm Finish Modal */}
            {showConfirmFinish && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full m-4 p-6">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Terminer l'entraînement ?</h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            Voulez-vous vraiment terminer ? Vous ne pourrez plus modifier vos réponses après l'envoi.
                        </p>
                        <div className="flex gap-2">
                            <button onClick={() => setShowConfirmFinish(false)} className="flex-1 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg">
                                Annuler
                            </button>
                            <button onClick={() => handleSubmit('manual')} disabled={isSubmitting} className="flex-1 py-2 bg-green-600 text-white rounded-lg disabled:opacity-50">
                                {isSubmitting ? 'Envoi…' : 'Terminer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AITrainingPage;
